import { createSelector } from '@reduxjs/toolkit'
import type { DraftState, WizardItemType, WizardSampleGroup } from '../../Model/OrderWizard/OrderWizardDraft.ts'
import { cleanPrimer } from '../../Util/orderSettingDetection/iupac.ts'
import { primersCompatible, stripAdapter } from '../../Util/orderSettingDetection/primerRegistry.ts'
import { labelNegativeControls } from './negativeControls.ts'
import { namesStem, sheetMatchOf } from './sheet/sheetMatching.ts'
import { comparableFileName, sampleStemOf } from '../../Util/fileNames.ts'
import { sheetRevision } from './sheet/sheetMapping.ts'
import { sheetPrimers } from './sheet/sheetImport.ts'
import { placedGroups } from './pools.ts'

/**
 * Sample archives: one R1/R2 pair holding several inline-tagged samples (COMBINED_SAMPLE_PAIRS). The
 * pipeline demultiplexes an archive by each sample's exact primers — anchored, exact, forward on R1 and
 * reverse on R2 — so a sample here is a sheet row with a name and both tagged primers.
 */

export const isArchiveOrder = (draft: DraftState): boolean => draft.settings?.sampleConfiguration.value === 'COMBINED_SAMPLE_PAIRS'

/**
 * Sheet rows per archive: by a file-name or sample-id column naming the archive's files, or — with no
 * such column and a single archive — every row. Sample names name samples, never archives.
 */
export const archiveRows = (draft: DraftState): { byGroup: Record<string, number[]>; unmatchedRows: number[] } => {
  const sheet = draft.sheet
  const groups = Object.values(draft.groups)
  const result: { byGroup: Record<string, number[]>; unmatchedRows: number[] } = { byGroup: {}, unmatchedRows: [] }
  if (!sheet) {
    return result
  }
  const { fileForward, fileReverse, sampleId } = sheet.mapping
  const candidates = groups.map((group) => {
    const names = [group.forward, group.reverse].flatMap((key) => (key && draft.files[key] ? [draft.files[key].name] : []))
    return { key: group.key, fileNames: names.map(comparableFileName), stem: sampleStemOf(names[0] ?? '') }
  })
  sheet.rows.forEach((row, index) => {
    const files = [fileForward, fileReverse]
      .map((column) => (column ? row[column]?.trim() : ''))
      .filter((value): value is string => !!value)
    const id = sampleId ? row[sampleId]?.trim() : ''
    let hits = candidates
    if (files.length > 0) {
      hits = candidates.filter((c) => files.some((file) => c.fileNames.includes(comparableFileName(file)) || sampleStemOf(file) === c.stem))
    } else if (id) {
      hits = candidates.filter((c) => namesStem(id, c.stem))
    } else if (candidates.length !== 1) {
      hits = []
    }
    if (hits.length === 1) {
      result.byGroup[hits[0].key] = [...(result.byGroup[hits[0].key] ?? []), index]
    } else {
      result.unmatchedRows.push(index)
    }
  })
  return result
}

/**
 * Sheet rows matched to the order's items, and those of them written into the order: by archive in an
 * archive order (its rows match the archive, never a sample's files), else by sample.
 */
export const sheetRowCounts = (draft: DraftState): { matched: number; written: number } => {
  const sheet = draft.sheet
  const placed = new Set(placedGroups(draft).map((group) => group.key))
  if (!sheet || placed.size === 0) {
    return { matched: 0, written: 0 }
  }
  const current = sheetRevision(sheet)
  const written = (key: string) => {
    const applied = draft.groups[key]?.applied
    return applied?.status === 'done' && applied.sheetKey === current
  }
  const rowsPerGroup: [string, number][] = isArchiveOrder(draft)
    ? Object.entries(archiveRows(draft).byGroup).map(([key, rows]) => [key, rows.length])
    : Object.keys(sheetMatchOf(draft)?.byGroup ?? {}).map((key) => [key, 1])
  return rowsPerGroup
    .filter(([key]) => placed.has(key))
    .reduce((counts, [key, rows]) => ({ matched: counts.matched + rows, written: counts.written + (written(key) ? rows : 0) }), {
      matched: 0,
      written: 0
    })
}

export type ArchiveSample = {
  /** Stable across sheet revisions: the sample name, numbered when it repeats. */
  key: string
  rowIndex: number
  name: string
  forwardPrimer?: string
  reversePrimer?: string
  forwardTag?: string
  reverseTag?: string
  itemType: WizardItemType
  typeSource: 'auto' | 'user'
}

const geneCandidates = (draft: DraftState, group: WizardSampleGroup, side: 'forward' | 'reverse'): string[] => {
  const primers = draft.discovery?.primers
  const pair = primers?.status === 'found' ? primers.pair : undefined
  const estimated = group.estimate?.[side].gene ?? undefined
  const swapped = !!pair && !!group.estimate?.forward.gene && pair.secondVariants.includes(group.estimate.forward.gene)
  const r1Variants = swapped ? pair?.secondVariants : pair?.firstVariants
  const r2Variants = swapped ? pair?.firstVariants : pair?.secondVariants
  const variants = (side === 'forward' ? r1Variants : r2Variants) ?? []
  return [...new Set([estimated, ...[...variants].sort((a, b) => b.length - a.length)].filter((gene): gene is string => !!gene))]
}

/** The tag of an exact primer: what precedes its gene primer, `undefined` when no known gene primer ends it. */
export const tagOf = (primer: string | undefined, genes: readonly string[]): string | undefined => {
  if (!primer) {
    return undefined
  }
  const clean = stripAdapter(primer)
  const gene = genes
    .map(cleanPrimer)
    .find((candidate) => clean.length >= candidate.length && primersCompatible(clean.slice(-candidate.length), candidate))
  return gene === undefined ? undefined : clean.slice(0, clean.length - gene.length)
}

/**
 * The samples a sheet defines for one archive, with their exact primers: from primer columns (a tag column
 * in front when the primer lacks it), or from tag columns alone plus the gene primer the reads showed.
 */
export const archiveSamplesOf = (
  draft: DraftState,
  group: WizardSampleGroup,
  rows: readonly number[]
): Omit<ArchiveSample, 'itemType' | 'typeSource'>[] => {
  const sheet = draft.sheet
  if (!sheet) {
    return []
  }
  const forwardGenes = geneCandidates(draft, group, 'forward')
  const reverseGenes = geneCandidates(draft, group, 'reverse')
  const seen = new Map<string, number>()
  return rows.map((rowIndex) => {
    const row = sheet.rows[rowIndex]
    const name = (sheet.mapping.sampleName ? row[sheet.mapping.sampleName]?.trim() : '') || `row ${rowIndex + 2}`
    const count = (seen.get(name) ?? 0) + 1
    seen.set(name, count)
    const declared = sheetPrimers(row, sheet.mapping)
    const tagOnly = (column: string | undefined, genes: string[]) => {
      const tag = column ? row[column]?.trim() : ''
      return tag && genes[0] ? tag + genes[0] : undefined
    }
    const forwardPrimer = declared.forward ?? tagOnly(sheet.mapping.forwardTag, forwardGenes)
    const reversePrimer = declared.reverse ?? tagOnly(sheet.mapping.reverseTag, reverseGenes)
    return {
      key: count === 1 ? name : `${name} #${count}`,
      rowIndex,
      name,
      forwardPrimer,
      reversePrimer,
      forwardTag: tagOf(forwardPrimer, forwardGenes),
      reverseTag: tagOf(reversePrimer, reverseGenes)
    }
  })
}

const computeArchiveSamples = (draft: DraftState): Record<string, ArchiveSample[]> => {
  const { byGroup } = archiveRows(draft)
  const perGroup = Object.fromEntries(
    Object.values(draft.groups).map((group) => [group.key, archiveSamplesOf(draft, group, byGroup[group.key] ?? [])] as const)
  )
  const all = Object.entries(perGroup).flatMap(([groupKey, samples]) =>
    samples.map((sample) => ({ key: `${groupKey}\u0000${sample.key}`, fileName: '', sampleName: sample.name }))
  )
  const { flagged, decision } = labelNegativeControls(all)
  const label = draft.controlsAnswer !== 'none' && (decision === 'auto' || draft.controlsAnswer === 'accept')
  const controls = new Set(label ? flagged : [])
  return Object.fromEntries(
    Object.entries(perGroup).map(([groupKey, samples]) => {
      const overrides = draft.groups[groupKey]?.archiveOverrides ?? {}
      return [
        groupKey,
        samples.map((sample): ArchiveSample => {
          const override = overrides[sample.key]
          return {
            ...sample,
            itemType: override ?? (controls.has(`${groupKey}\u0000${sample.key}`) ? 'NEGATIVE_CONTROL' : 'SAMPLE'),
            typeSource: override ? 'user' : 'auto'
          }
        })
      ]
    })
  )
}

export type ArchiveCheck = {
  /** Sheet tag pairs the scanned reads never show. */
  missingInReads: string[]
  /** Frequent read tag pairs no sheet sample declares, with their share of located read pairs. */
  undefinedInSheet: { tags: string; share: number }[]
  /** Tag pairs two sheet samples share. */
  duplicates: string[]
  /** Sheet samples without both primers. */
  incomplete: string[]
  coverage: number
}

const FREQUENT_SHARE = 0.02
const pairLabel = (forward: string, reverse: string) => `${forward || '∅'}+${reverse || '∅'}`

/**
 * An archive's sheet samples against the tag pairs its reads carry. Exact comparison on purpose: the
 * pipeline demultiplexes with anchored, error-free matches, so a tag one base short finds no reads there.
 */
export const checkArchive = (group: WizardSampleGroup, samples: readonly ArchiveSample[]): ArchiveCheck => {
  const labels = samples.flatMap((sample) =>
    sample.forwardTag !== undefined && sample.reverseTag !== undefined ? [pairLabel(sample.forwardTag, sample.reverseTag)] : []
  )
  const counts = new Map<string, number>()
  labels.forEach((label) => counts.set(label, (counts.get(label) ?? 0) + 1))
  const census = group.archiveTags
  const inReads = new Map((census?.tags ?? []).map((tag) => [pairLabel(tag.forwardTag, tag.reverseTag), tag.share]))
  const declared = new Set(labels)
  return {
    missingInReads: census ? [...declared].filter((label) => !inReads.has(label)) : [],
    undefinedInSheet: census
      ? [...inReads].filter(([label, share]) => share >= FREQUENT_SHARE && !declared.has(label)).map(([tags, share]) => ({ tags, share }))
      : [],
    duplicates: [...counts].filter(([, count]) => count > 1).map(([label]) => label),
    incomplete: samples.filter((sample) => !sample.forwardPrimer || !sample.reversePrimer).map((sample) => sample.name),
    coverage: census ? [...inReads].filter(([label]) => declared.has(label)).reduce((sum, [, share]) => sum + share, 0) : 0
  }
}

/** Every archive's samples with their labels: sample names through the strict control families. */
export const archiveSamples = createSelector(
  [
    (draft: DraftState) => draft.sheet,
    (draft: DraftState) => draft.groups,
    (draft: DraftState) => draft.files,
    (draft: DraftState) => draft.discovery,
    (draft: DraftState) => draft.controlsAnswer
  ],
  (sheet, groups, files, discovery, controlsAnswer) =>
    computeArchiveSamples({ sheet, groups, files, discovery, controlsAnswer } as DraftState)
)

/** Sheet rows of negative controls, which get no metadata: by archive sample in an archive order, else by matched item. */
export const controlSheetRows = (draft: DraftState): Set<number> => {
  if (isArchiveOrder(draft)) {
    return new Set(
      Object.values(archiveSamples(draft))
        .flat()
        .filter((sample) => sample.itemType === 'NEGATIVE_CONTROL')
        .map((sample) => sample.rowIndex)
    )
  }
  const byGroup = sheetMatchOf(draft)?.byGroup ?? {}
  return new Set(
    Object.entries(byGroup)
      .filter(([key]) => draft.groups[key]?.itemType === 'NEGATIVE_CONTROL')
      .map(([, rowIndex]) => rowIndex)
  )
}
